import React from 'react';
import { Award, ShieldCheck, BookOpen, Star, Users, Clock, CheckCircle, TrendingUp } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

export default function EEATSection() {
  const credentials = [
    {
      icon: Clock,
      title: 'Résultats en 7 jours',
      description: 'Retour affectif rapide garanti',
      stat: '7j'
    },
    {
      icon: Users,
      title: '15,000+ clients',
      description: 'Couples réunis avec succès',
      stat: '15K+'
    },
    {
      icon: Star,
      title: 'Taux de réussite',
      description: 'Satisfaction client garantie',
      stat: '99%'
    },
    {
      icon: TrendingUp,
      title: 'Clients satisfaits',
      description: 'Recommandent Maître Diamma',
      stat: '99%'
    }
  ];

  const expertise = [
    'Retour affectif rapide en 7 jours',
    'Voyance et médiumnité héréditaire',
    'Désenvoûtement et purification',
    'Protection spirituelle durable',
    'Rituels ancestraux africains',
    'Guérison par médecines traditionnelles'
  ];

  const trustSignals = [
    { icon: ShieldCheck, label: 'Confidentialité RGPD' },
    { icon: CheckCircle, label: 'Résultats garantis' },
    { icon: Award, label: 'Marabout certifié' },
    { icon: BookOpen, label: 'Savoirs ancestraux' }
  ];

  return (
    <section id="expertise" className="py-12 bg-gradient-to-b from-black to-gray-900 relative">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-yellow-600 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-yellow-500/10 border-yellow-500/30 text-yellow-500">
            Expertise Reconnue
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Pourquoi choisir notre expert ?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            30 années de pratique, des milliers de couples réunis, et une réputation d'excellence dans toute la région.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {credentials.map((cred, idx) => {
            const Icon = cred.icon;
            return (
              <Card 
                key={idx} 
                className="p-6 text-center bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 hover:border-yellow-500/40 transition-all group"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-500/20 transition-colors">
                  <Icon className="h-6 w-6 text-yellow-500" />
                </div>
                <p className="text-3xl font-bold text-yellow-500 mb-2">{cred.stat}</p>
                <h3 className="text-sm font-semibold text-white mb-1">{cred.title}</h3>
                <p className="text-xs text-gray-500">{cred.description}</p>
              </Card>
            );
          })}
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Expertise Column */}
          <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-yellow-500" />
              </div>
              <h3 className="text-xl font-bold text-white">Domaines d'Expertise</h3>
            </div>
            <div className="space-y-3">
              {expertise.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-yellow-500/10 hover:border-yellow-500/30 transition-colors">
                  <CheckCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Authority & Trust Column */}
          <div className="space-y-6">
            {/* Authority */}
            <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <Award className="h-5 w-5 text-yellow-500" />
                </div>
                <h3 className="text-xl font-bold text-white">Autorité Reconnue</h3>
              </div>
              <p className="text-gray-400 mb-4">
                Le Maître est reconnu comme l'un des <strong className="text-yellow-500">marabouts les plus sérieux</strong> de la région. Sa réputation s'est construite sur des <strong className="text-yellow-500">résultats concrets</strong> et le bouche-à-oreille de clients satisfaits.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="border-yellow-500/30 text-yellow-500"><span className="seo-hidden">Marseille</span></Badge>
                <Badge variant="outline" className="border-yellow-500/30 text-yellow-500">Aix-en-Provence</Badge>
                <Badge variant="outline" className="border-yellow-500/30 text-yellow-500">Bouches-du-Rhône</Badge>
                <Badge variant="outline" className="border-yellow-500/30 text-yellow-500">PACA</Badge>
              </div>
            </Card>

            {/* Trust Signals */}
            <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-yellow-500" />
                </div>
                <h3 className="text-xl font-bold text-white">Garanties de Confiance</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {trustSignals.map((signal, idx) => {
                  const Icon = signal.icon;
                  return (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-black/30 rounded-lg">
                      <Icon className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                      <span className="text-sm text-gray-300">{signal.label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>

        {/* Bottom SEO Text */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm max-w-4xl mx-auto">
            <strong className="text-yellow-500/70">Marabout</strong> • <strong className="text-yellow-500/70">Voyant</strong> • <strong className="text-yellow-500/70">Médium</strong> • Expert en retour affectif, voyance, et désenvoûtement. Consultations disponibles.<span className="seo-hidden"> Marseille Bouches-du-Rhône</span>
          </p>
        </div>
      </div>
    </section>
  );
}
